import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button as MButton,
  ThemeProvider,
} from '@mui/material';
import { customButtonTheme, customRedButtonTheme } from 'assets/styles/themes/generic_button_mui';
import { useContext, useEffect, useRef, useState } from 'react';
import Button from 'components/generics/Button';
import MapParcels from 'components/generics/Map/MapParcels';
import {
  cancelProcedure,
  finalizeProcedure,
  getParcelsByExploitation,
} from 'services/API/Exploitation';
import { ExploitationContext } from 'utils/context';
import useDataGridCommonListActions from 'utils/Datagrid/useDataGridCommonListActions';
import AddEditParcelModal from './form/AddEditParcelModal';
import dayjs from 'dayjs';
import DataGridParcels from './DataGridParcels';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  filterParcelFromActivityDate,
  getCultureCampaignKey,
} from 'utils/Datagrid/CustomParcelstDataGridUtils';
import { ClearIcon } from '@mui/x-date-pickers-pro';
import { customDialogTheme } from 'assets/styles/themes/Dialog/generic_dialog_mui';
import DoneIcon from '@mui/icons-material/Done';
import { toast } from 'react-toastify';
import { CustomToolbarProcedure } from 'utils/Datagrid/CustomParcelProcedureDataGridUtils';
import { displayErrorMessage } from 'utils/tools_functions';
import { isSameAsOriginal } from 'utils/Datagrid/CustomForecastDataGridUtils';
import { getMappingFiles } from 'services/API/Cooperative';
import { useTranslation } from 'react-i18next';

const ProcedureCampaign = ({ exploitationSelected, loadDataExploitation }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const [dataGridLoading, setDataGridLoading] = useState(false);
  const stackedParcelsRequestsRef = useRef([]);
  const showMap = (bool) => {
    setMapTab(bool);
  };
  const nextForecastCampaign = location.state?.nextCampaignChosen;
  const indexForNextCampaign =
    nextForecastCampaign?.startYear - exploitationSelected?.forecastCampaign?.startYear;
  const typeProcedure = indexForNextCampaign > 0 ? 'forward' : 'backward';
  const hideFormData = typeProcedure === 'forward' ? ['forecast', 'isFixed'] : [];
  const [parcelsForProcedure, setParcelsForProcedure] = useState(
    exploitationSelected
      ? structuredClone(
          filterParcelFromActivityDate(exploitationSelected?.parcels, nextForecastCampaign),
        )
      : [],
  );
  const navigate = useNavigate();

  const [parcelUpdated, setParcelUpdated] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [mappingFiles, setMappingFiles] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [openFinalizeDialog, setOpenFinalizeDialog] = useState(false);
  const [actionId, setActionId] = useState(0);
  const [editData, setEditData] = useState(null);
  const [modal, setModal] = useState(false);
  const { setExploitationContext, getGroundTypesList } = useContext(ExploitationContext);

  const cooperativeId = localStorage.getItem('cooperative');
  const processedRowsRef = useRef([]);

  const processedRows = parcelsForProcedure.map((parcel) => {
    return {
      id: parcel?.id,
      name: parcel?.name,
      surface: parcel?.surface,
      parcelId: parcel?.id,
      isIrrigable: parcel?.isIrrigable,
      groundType: parcel?.groundType,
      previousCulture2:
        parcel?.parcelCultures[
          getCultureCampaignKey(2 - indexForNextCampaign, exploitationSelected?.forecastCampaign)
        ] || 'EMPTY',
      previousCulture1:
        parcel?.parcelCultures[
          getCultureCampaignKey(1 - indexForNextCampaign, exploitationSelected?.forecastCampaign)
        ] || 'EMPTY',
      forecast:
        parcel?.parcelCultures[
          getCultureCampaignKey(0 - indexForNextCampaign, exploitationSelected?.forecastCampaign)
        ]?.id || '0',
      isFixed:
        parcel?.parcelCultures[
          getCultureCampaignKey(0 - indexForNextCampaign, exploitationSelected?.forecastCampaign)
        ]?.isFixed || false,
      organicState: parcel?.organicState,
      parentId: parcel?.parent,
      parent: parcel?.parent,
      isParent: parcel?.isParent,
      parcelCultures: parcel?.parcelCultures,
      conversionDate: parcel?.conversionDate,
      coordinates: parcel?.coordinates,
    };
  });

  useEffect(() => {
    if (cooperativeId !== '') {
      getMappingFiles(cooperativeId)
        .then((resp) => {
          setMappingFiles(resp?.data?.data);
        })
        .catch((err) => {
          toast.error(t('errors.ERR_GET_MAPPING_FILES'));
        });
    }
  }, []);

  useEffect(() => {
    processedRowsRef.current = processedRows;
  }, [processedRows]);

  const updateMapParcel = (parcelId) => {
    const parcelToUpdate = processedRowsRef.current.find((parcel) => parcel.id === parcelId);
    setEditData({
      name: parcelToUpdate?.name,
      surface: parcelToUpdate?.surface,
      isIrrigable: parcelToUpdate?.isIrrigable,
      groundType: parcelToUpdate?.groundType.id || null,
      previousCulture2: parcelToUpdate?.previousCulture2?.id || 'EMPTY',
      previousCulture1: parcelToUpdate?.previousCulture1?.id || 'EMPTY',
      forecast: parcelToUpdate?.forecast || '0',
      isFixed: parcelToUpdate?.isFixed,
      id: parcelToUpdate?.id,
      parentId: parcelToUpdate?.parentId,
      isParent: parcelToUpdate?.isParent,
      conversionDate: parcelToUpdate?.conversionDate,
      organicState: parcelToUpdate?.organicState,
    });
    setModal(true);
  };

  const columnsMetaData = [
    { field: 'name', position: 1, editable: false },
    { field: 'surface', position: 2, editable: false },
    { field: 'previousCulture1', position: 3, editable: false },
    { field: 'actions', position: 4, editable: false },
  ];

  const generateRandomId = (excludedId) => {
    let random;
    while (!random) {
      const x = Math.floor(Math.random() * Date.now());
      if (!excludedId.includes(x)) {
        random = x;
      }
    }
    return random;
  };

  const formSuccess = async (formDataModal) => {
    if (!formDataModal.id) {
      const idNewParcels = generateRandomId(parcelsForProcedure.map((parcel) => parcel.id));
      stackedParcelsRequestsRef.current.push({
        ...formDataModal,
        id: idNewParcels,
        create: true,
      });
      parcelsForProcedure.push({
        ...formDataModal,
        id: idNewParcels,
      });
    } else {
      setParcelsForProcedure(
        parcelsForProcedure.map((parcel) => {
          if (parcel.id === formDataModal.id) {
            let stackedRequestCurrent = stackedParcelsRequestsRef.current.find(
              (request) => request.id === parcel.id,
            ) || { parcelCultures: {} };

            for (const key of Object.keys(formDataModal)) {
              if (key === 'parcelCultures') {
                for (const campaignRange of Object.keys(formDataModal[key])) {
                  if (formDataModal[key][campaignRange]?.name) {
                    parcel.parcelCultures[campaignRange] = formDataModal[key][campaignRange];
                    stackedRequestCurrent.parcelCultures[campaignRange] =
                      formDataModal.parcelCultures[campaignRange];
                  }
                }
              } else {
                stackedRequestCurrent[key] = formDataModal[key];
              }
            }
            if (
              !stackedParcelsRequestsRef.current.find(
                (request) => request.id === stackedRequestCurrent.id,
              )
            ) {
              stackedParcelsRequestsRef.current.push({
                ...stackedRequestCurrent,
              });
              // we enter else clause when parcel already exist in stackedParcelsRequestsRef, so this is an edit
            } else {
              // check if the new value is same as original value and if it is, delete the key from stackedParcelsRequestsRef
              for (const key of Object.keys(formDataModal)) {
                if (key === 'parcelCultures') {
                  const originalParcel = exploitationSelected?.parcels.find(
                    (parcel) => parcel.id === formDataModal.id,
                  );
                  if (originalParcel !== undefined) {
                    for (const campaignRange of Object.keys(formDataModal[key])) {
                      if (
                        originalParcel?.[key]?.[campaignRange]?.id ===
                        formDataModal[key]?.[campaignRange]?.id
                      ) {
                        delete stackedRequestCurrent[key][campaignRange];
                      }
                    }
                  }
                } else if (isSameAsOriginal(key, exploitationSelected?.parcels, formDataModal)) {
                  delete stackedRequestCurrent[key];
                }
              }
            }
            delete formDataModal['parcelCultures'];
            // if an entry in stackedParcelsRequestsRef only has id and empty parcelcultures key we delete the entry from stackedParcelsRequestsRef
            stackedParcelsRequestsRef.current = stackedParcelsRequestsRef.current.filter((obj) => {
              const keys = Object.keys(obj);
              const hasOnlyIdAndParcelCultures =
                keys.length === 2 && keys.includes('id') && keys.includes('parcelCultures');
              const isParcelCulturesEmpty = Object.keys(obj.parcelCultures || {}).length === 0;

              return !(hasOnlyIdAndParcelCultures && isParcelCulturesEmpty);
            });
            return { ...parcel, ...formDataModal };
          }
          if (parcel?.parent === formDataModal.id) {
            for (const key of Object.keys(formDataModal)) {
              if (key === 'parcelCultures') {
                for (const campaignRange of Object.keys(formDataModal[key])) {
                  if (formDataModal[key][campaignRange]?.name) {
                    parcel.parcelCultures[campaignRange] = formDataModal[key][campaignRange];
                  }
                }
              }
            }
            const updatedKey = {
              ...(formDataModal.organicState !== undefined && {
                organicState: formDataModal.organicState,
              }),
              ...(formDataModal.conversionDate !== undefined && {
                conversionDate: formDataModal.conversionDate,
              }),
              ...(formDataModal.groundType !== undefined && {
                groundType: formDataModal.groundType,
              }),
            };

            return { ...parcel, ...updatedKey };
          }
          return parcel;
        }),
      );
    }

    setModal(false);
    setEditData(null);
  };
  const [coordsExist, setCoordsExist] = useState(false);
  const [mapTab, setMapTab] = useState(false);

  const renderDeleteDialog = () => {
    function handleDeleteClick(event) {
      const parcelToDelete = parcelsForProcedure.find((parcel) => parcel.id === actionId);
      if (parcelToDelete) {
        const stackedToDelete = stackedParcelsRequestsRef.current.find(
          (request) => request.id === parcelToDelete.id,
        );
        if (stackedToDelete) {
          if (stackedToDelete.create) {
            stackedParcelsRequestsRef.current = stackedParcelsRequestsRef.current.filter(
              (request) => request.id !== parcelToDelete.id,
            );
          } else {
            stackedParcelsRequestsRef.current = stackedParcelsRequestsRef.current.map((request) => {
              if (request.id === parcelToDelete.id) {
                return {
                  id: request.id,
                  delete: true,
                };
              }
              return request;
            });
          }
        } else {
          stackedParcelsRequestsRef.current.push({
            id: parcelToDelete.id,
            delete: true,
          });
        }

        // regen parcels to reset depth tree data when a parent is delete
        setParcelsForProcedure(
          parcelsForProcedure
            .filter((parcel) => parcel.id !== actionId)
            .map((parcel) =>
              parcel.parent === parcelToDelete.id
                ? { ...parcel, parent: null, isParent: false }
                : parcel,
            ),
        );
      }
      setOpenDeleteDialog(false);
    }

    return (
      <ThemeProvider theme={customDialogTheme}>
        <Dialog maxWidth="md" open={openDeleteDialog}>
          <DialogTitle>{t('common.warning')}</DialogTitle>
          <DialogContent dividers>
            <h3>{t('title.deleteParcel')}</h3>
            <p>{t('message.confirmDeleteParcel')}</p>
            <p>{t('dialog.continueQuestion')}</p>
          </DialogContent>
          <DialogActions>
            <MButton onClick={handleDeleteClick}>
              {t('common.delete')} <DoneIcon />
            </MButton>
            <MButton onClick={() => setOpenDeleteDialog(false)}>
              {t('common.no')} <ClearIcon />
            </MButton>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    );
  };

  const RenderFinalizeProcedureModal = () => {
    function finalizeProcedure() {
      setOpenFinalizeDialog(false);
      handleSave();
    }

    const previousCampaign = getCultureCampaignKey(
      1 - indexForNextCampaign,
      exploitationSelected?.forecastCampaign,
    );
    const hasMissingPreviousCulture1 = parcelsForProcedure?.some(
      (parcel) => parcel?.parcelCultures?.[previousCampaign] === null,
    );
    return (
      <ThemeProvider theme={customDialogTheme}>
        <Dialog maxWidth="md" open={openFinalizeDialog}>
          {hasMissingPreviousCulture1 && <DialogTitle>{t('common.warning')}</DialogTitle>}
          <DialogContent dividers>
            <h3>{t('dialog.validateProcedureQuestion')}</h3>
            {hasMissingPreviousCulture1 && (
              <p>{t('dialog.missingPreviousCultureMessage', { campaign: previousCampaign })}</p>
            )}
          </DialogContent>
          <DialogActions>
            <MButton onClick={finalizeProcedure}>
              {t('common.validate')} <DoneIcon />
            </MButton>
            <MButton onClick={() => setOpenFinalizeDialog(false)}>
              {t('common.cancel')} <ClearIcon />
            </MButton>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    );
  };
  const { getActions } = useDataGridCommonListActions(
    processedRows.map((row) => {
      return {
        ...row,
        previousCulture2: row?.previousCulture2?.id,
        previousCulture1: row?.previousCulture1?.id,
        groundType: row?.groundType?.id,
      };
    }),
    setModal,
    setIsEdit,
    setEditData,
    setOpenDeleteDialog,
    setActionId,
    { typeProcedure },
  );

  useEffect(() => {
    if (editData && !modal) {
      setEditData(null);
    }
  }, [modal]);

  useEffect(() => {
    if (!exploitationSelected) {
      loadDataExploitation(
        false,
        Number(localStorage.getItem('exploitation')),
        nextForecastCampaign?.stringFormat,
      );
    } else {
      loadData();
    }
  }, [exploitationSelected]);

  const loadData = async () => {
    setParcelsForProcedure(
      structuredClone(
        filterParcelFromActivityDate(exploitationSelected?.parcels, nextForecastCampaign),
      ),
    );
    getGroundTypesList();
    if (!exploitationSelected?.parcels?.find((p) => p.coordinates?.length > 0)) {
      const result = await getParcelsByExploitation(Number(localStorage.getItem('exploitation')));
      setCoordsExist(result.data.data.find((p) => p.coordinates?.length > 0));
    } else {
      setCoordsExist(exploitationSelected?.parcels?.find((p) => p.coordinates?.length > 0));
    }
    setParcelUpdated(!parcelUpdated);
  };
  const handleSave = async () => {
    for (const currentParcel of stackedParcelsRequestsRef.current) {
      if (currentParcel.create && currentParcel.id) {
        delete currentParcel?.create;
        delete currentParcel?.id;
      }
      currentParcel.groundType = currentParcel.groundType?.id;
    }
    try {
      const result = await finalizeProcedure(exploitationSelected?.id, {
        parcels: stackedParcelsRequestsRef.current,
        forecast: {
          startYear: nextForecastCampaign?.startYear,
          endYear: nextForecastCampaign?.endYear,
        },
      });
      toast.success(t('success.procedureCompleted'));
      setExploitationContext((prevContext) => ({
        ...prevContext,
        ...result.data.data.exploitation,
        parcels: result.data.data.parcels,
        forecastCampaign: result.data.data.exploitation.forecastCampaign,
        possibleCampaignChange: result.data.data.exploitation.possibleCampaignChange,
      }));
      navigate('/exploitation/parcels', {
        state: {
          exploitContextReloadNeeded: false,
        },
      });
    } catch (error) {
      const errorResponse = error.response.data;
      if (
        errorResponse.data?.error_type?.match('ERR_HAS_BEEN_TAKEN_OVER_ENDED') ||
        errorResponse.data?.error_type?.match('ERR_HAS_BEEN_TAKEN_OVER_STARTED')
      ) {
        toast.error(displayErrorMessage(errorResponse.data.error_type, errorResponse.data));
        navigate('/exploitation/parcels');
      } else {
        toast.error(errorResponse.message);
      }
    }
  };

  const handleCancel = async () => {
    try {
      const result = await cancelProcedure(exploitationSelected?.id);
      toast.success(t('success.procedureCancelled'));
      setOpenCancelDialog(false);
      if (result) navigate('/exploitation/parcels');
    } catch (error) {
      toast.error(t('errors.genericError'));
    }
  };

  const renderCancelProcedure = () => {
    return (
      <ThemeProvider theme={customDialogTheme}>
        <Dialog maxWidth="md" open={openCancelDialog}>
          <DialogTitle>{t('common.warning')}</DialogTitle>
          <DialogContent dividers>
            <h3>{t('dialog.procedureCancellationTitle')}</h3>
            <p>{t('dialog.procedureCancellationMessage')}</p>
            <p>{t('dialog.continueQuestion')}</p>
          </DialogContent>
          <DialogActions>
            <MButton onClick={handleCancel}>
              {t('button.confirmCancellation')} <DoneIcon />
            </MButton>
            <MButton onClick={() => setOpenCancelDialog(false)}>
              {t('button.stayOnProcedure')} <ClearIcon />
            </MButton>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
    );
  };

  return (
    <div className="section">
      <div className="main_container">
        <div className="contain-header">
          {!!coordsExist && (
            <div className="map_button_container">
              <Button
                defaultStyle="green_button"
                text={t('common.parcels')}
                backgroundColor={mapTab ? 'white' : '#277134'}
                textColor={mapTab ? '#277134' : 'white'}
                onClick={() => {
                  showMap(false);
                }}
              />
              <Button
                defaultStyle="green_button"
                text={t('common.map')}
                textColor={mapTab ? 'white' : '#277134'}
                backgroundColor={mapTab ? '#277134' : 'white'}
                onClick={() => {
                  showMap(true);
                }}
              />
            </div>
          )}
          {renderDeleteDialog()}
          {renderCancelProcedure()}
          <div className="parcels_button_container">
            <ThemeProvider theme={customRedButtonTheme}>
              <MButton
                id="inputCostButton"
                onClick={() => {
                  setOpenCancelDialog(true);
                }}
              >
                {t('button.cancelProcedure')}
              </MButton>
            </ThemeProvider>
            <ThemeProvider theme={customButtonTheme}>
              <MButton id="inputCostButton" onClick={() => setOpenFinalizeDialog(true)}>
                {t('button.finalizeProcedure')}
              </MButton>
            </ThemeProvider>
            {openFinalizeDialog && <RenderFinalizeProcedureModal />}
          </div>
        </div>
        {!mapTab ? (
          <div>
            <div className="title_spacing">
              <h2>
                {t('title.campaignToSimulate')}: {nextForecastCampaign?.startYear}-
                {nextForecastCampaign?.endYear}
              </h2>
            </div>
            <DataGridParcels
              parcels={processedRows}
              forecastCampaign={nextForecastCampaign}
              cultures={exploitationSelected?.cultures}
              groundTypes={exploitationSelected?.groundTypes}
              id={exploitationSelected?.id}
              refreshData={loadData}
              dataGridLoading={dataGridLoading}
              setEditData={setEditData}
              columnsMetadata={columnsMetaData}
              stackedParcelsRequestsRef={stackedParcelsRequestsRef}
              maxDate={dayjs().year(Number(nextForecastCampaign?.startYear)).month(6).date(31)}
              getActions={getActions}
              customToolbar={
                <CustomToolbarProcedure
                  setModal={setModal}
                  typeProcedure={typeProcedure}
                  mappingFiles={mappingFiles}
                  nextForecastCampaign={nextForecastCampaign}
                />
              }
            />
          </div>
        ) : (
          <div>
            {!!coordsExist && (
              <div>
                <MapParcels
                  updateParcel={updateMapParcel}
                  forecastCampaign={nextForecastCampaign}
                  fromProcedure={true}
                  parcelListData={processedRows}
                ></MapParcels>
              </div>
            )}
          </div>
        )}
        {modal && (
          <AddEditParcelModal
            modal={modal}
            setModal={setModal}
            onSubmitSuccess={formSuccess}
            edit={editData}
            maxDate={dayjs().year(Number(nextForecastCampaign?.startYear)).month(6).date(31)}
            forecastCampaign={nextForecastCampaign}
            from={'procedure'}
            hideFormData={hideFormData}
          />
        )}
      </div>
    </div>
  );
};

export default ProcedureCampaign;
